import { useCallback, useEffect, useState } from 'react'
import {
  $getSelection,
  $isRangeSelection,
  FORMAT_TEXT_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  SELECTION_CHANGE_COMMAND,
  UNDO_COMMAND,
  REDO_COMMAND,
  CAN_UNDO_COMMAND,
  CAN_REDO_COMMAND,
  $createParagraphNode,
  INDENT_CONTENT_COMMAND,
  OUTDENT_CONTENT_COMMAND,
  ElementFormatType,
  $isElementNode,
} from 'lexical'
import { $isHeadingNode, $createHeadingNode, HeadingTagType } from '@lexical/rich-text'
import { $setBlocksType } from '@lexical/selection'
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
  $isListNode,
} from '@lexical/list'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { mergeRegister } from '@lexical/utils'
import './toolbar.css'
import { handleExportPDF, handleExportWord } from './utils'
import {
  Bold,
  Download,
  IndentDecrease,
  IndentIncrease,
  Italic,
  List,
  ListOrdered,
  Redo,
  Strikethrough,
  Underline,
  Undo,
  ChevronDown,
  Highlighter,
  AlignLeft,
  AlignCenter,
  AlignRight,
} from 'lucide-react'
import { APPLY_ALL_SUGGESTIONS_COMMAND, REJECT_ALL_SUGGESTIONS_COMMAND } from './plugins/SuggestionPlugin'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Switch } from '@heroui/react'
import { CommentNode, $isCommentNode } from '@/components/edit/nodes/CommentNode'
import { $isSuggestionNode } from './nodes/SuggestionNode'
import { useParams } from 'react-router-dom'
import { useBilling } from '@/hooks/useBilling'
import { useTemporaryStore } from '@/contexts/temporaryStore'

interface ToolbarPluginProps {
  onSaveFile: () => void
  isTrackChangesEnabled: boolean
  setIsTrackChangesEnabled: (some: any) => void
  setHasPendingSuggestions: (hasPendingSuggestions: boolean) => void
  hideTrackChanges?: boolean
  documentName?: string
}

/** Note: For text alignment we default to `left` if there is no explicit
 * alignment set for that element in the document. However, if we deal with
 * non-left-to-right text in a contract (or something is imported from
 * CloudConvert with center-aligned text, for example), the element's alignment
 * will incorrectly appear to be left-aligned according to the UI. */

export default function ToolbarPlugin({
  onSaveFile,
  isTrackChangesEnabled,
  setIsTrackChangesEnabled,
  setHasPendingSuggestions,
  hideTrackChanges = true,
  documentName,
}: ToolbarPluginProps) {
  const [editor] = useLexicalComposerContext()
  const [isBold, setIsBold] = useState(false)
  const [isItalic, setIsItalic] = useState(false)
  const [isUnderline, setIsUnderline] = useState(false)
  const [isStrikethrough, setIsStrikethrough] = useState(false)
  const [isHighlighted, setIsHighlighted] = useState(false)
  const [blockType, setBlockType] = useState('paragraph')
  const [commentCount, setCommentCount] = useState(0)
  const [suggestionCount, setSuggestionCount] = useState(0)
  const [canUndo, setCanUndo] = useState(false)
  const [canRedo, setCanRedo] = useState(false)
  const [elementFormat, setElementFormat] = useState<ElementFormatType>('left')
  const { id: documentPublicId } = useParams()
  const { subscription, onSubscribe } = useBilling()
  const { currentDocumentMetadata, productMetadata, setCurrentDocumentMetadata } = useTemporaryStore(s => ({
    currentDocumentMetadata: s.currentDocumentMetadata,
    productMetadata: s.productMetadata,
    setCurrentDocumentMetadata: s.setCurrentDocumentMetadata,
  }))

  // Helper function to create clean filename from document name
  const getCleanFilename = (extension: string = '') => {
    if (!documentName) return `document${extension}`

    // Remove file extension if present and clean the name
    const nameWithoutExt = documentName.replace(/\.[^/.]+$/, '')
    const cleanName = nameWithoutExt
      .replace(/[^a-zA-Z0-9\s\-_]/g, '') // Remove special characters
      .replace(/\s+/g, '_') // Replace spaces with underscores
      .trim()

    return cleanName ? `${cleanName}${extension}` : `document${extension}`
  }

  // Function to count comments and suggestions in the editor
  const updateCounts = useCallback(() => {
    editor.getEditorState().read(() => {
      const nodeMap = editor.getEditorState()._nodeMap
      let commentCount = 0
      let suggestionCount = 0

      nodeMap.forEach(node => {
        if ($isCommentNode(node)) {
          commentCount++
        } else if ($isSuggestionNode(node)) {
          suggestionCount++
        }
      })

      setCommentCount(commentCount)
      setSuggestionCount(suggestionCount)
      setHasPendingSuggestions(suggestionCount > 0)
    })
  }, [editor])

  // Listen for editor state changes to update counts
  useEffect(() => {
    const removeUpdateListener = editor.registerUpdateListener(() => {
      updateCounts()
    })

    // Initial count
    updateCounts()

    return removeUpdateListener
  }, [editor, updateCounts])

  // Listen for selection changes to update toolbar state
  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        updateToolbar()
        return false
      },
      COMMAND_PRIORITY_CRITICAL
    )
  }, [editor])

  // Listen for undo/redo state changes
  useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        CAN_UNDO_COMMAND,
        payload => {
          setCanUndo(payload)
          return false
        },
        COMMAND_PRIORITY_CRITICAL
      ),
      editor.registerCommand(
        CAN_REDO_COMMAND,
        payload => {
          setCanRedo(payload)
          return false
        },
        COMMAND_PRIORITY_CRITICAL
      )
    )
  }, [editor])

  // Update toolbar state based on selection
  const updateToolbar = useCallback(() => {
    const selection = $getSelection()

    if ($isRangeSelection(selection)) {
      // Update text format states
      setIsBold(selection.hasFormat('bold'))
      setIsItalic(selection.hasFormat('italic'))
      setIsUnderline(selection.hasFormat('underline'))
      setIsStrikethrough(selection.hasFormat('strikethrough'))
      setIsHighlighted(selection.hasFormat('highlight'))

      // Update block type state
      const anchorNode = selection.anchor.getNode()
      const element = anchorNode.getKey() === 'root' ? anchorNode : anchorNode.getTopLevelElementOrThrow()

      const elementKey = element.getKey()
      const elementDOM = editor.getElementByKey(elementKey)

      if (elementDOM !== null) {
        if ($isHeadingNode(element)) {
          const tag = element.getTag()
          setBlockType(tag)
        } else {
          setBlockType('paragraph')
        }

        // Update element format (alignment) state
        if ($isElementNode(element)) {
          const formatType = element.getFormatType()
          // Default to 'left' if no format is explicitly set
          setElementFormat(formatType || 'left')
        } else {
          // Default to 'left' for non-element nodes
          setElementFormat('left')
        }
      }
    }
  }, [editor])

  // Format text functions
  const formatText = (
    format: 'bold' | 'italic' | 'underline' | 'strikethrough' | 'highlight' | 'subscript' | 'superscript' | 'code'
  ) => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, format)
  }

  // Format heading function
  const formatHeading = (headingSize: HeadingTagType) => {
    editor.update(() => {
      const selection = $getSelection()
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createHeadingNode(headingSize))
      }
    })
  }

  // Format paragraph function
  const formatParagraph = () => {
    editor.update(() => {
      const selection = $getSelection()
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createParagraphNode())
      }
    })
  }

  // List functions
  const formatList = (listType: string) => {
    editor.update(() => {
      const selection = $getSelection()
      if (!$isRangeSelection(selection)) return

      const anchorNode = selection.anchor.getNode()
      let listNode = null
      let currentNode = anchorNode

      // Traverse up to find a list node
      while (currentNode && !$isListNode(currentNode)) {
        const parent = currentNode.getParent()
        if (!parent) break
        currentNode = parent
      }

      if (currentNode && $isListNode(currentNode)) {
        listNode = currentNode
      }

      if (listNode) {
        // We're already in a list
        const currentListType = listNode.getListType()
        const targetType = listType === 'ul' ? 'bullet' : 'number'

        if (currentListType === targetType) {
          // Same list type - remove the list
          editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined)
        } else {
          // Different list type - convert to new type
          if (listType === 'ul') {
            editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)
          } else if (listType === 'ol') {
            editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)
          }
        }
      } else {
        // Not in a list - create new list
        if (listType === 'ul') {
          editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)
        } else if (listType === 'ol') {
          editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)
        }
      }
    })
  }

  const undo = () => {
    editor.dispatchCommand(UNDO_COMMAND, undefined)
  }

  const redo = () => {
    editor.dispatchCommand(REDO_COMMAND, undefined)
  }

  // Indentation functions
  const indentContent = () => {
    editor.dispatchCommand(INDENT_CONTENT_COMMAND, undefined)
  }

  const outdentContent = () => {
    editor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined)
  }

  // Comment management functions
  const resolveAllComments = () => {
    editor.update(
      () => {
        // Find all comment nodes in the editor using node traversal
        const root = editor.getEditorState()._nodeMap.get('root')
        const commentNodes: CommentNode[] = []

        const collectCommentNodes = (node: any) => {
          if (node instanceof CommentNode) {
            commentNodes.push(node)
          }
          const children = node.getChildren?.()
          if (children) {
            for (const child of children) {
              collectCommentNodes(child)
            }
          }
        }

        if (root) {
          collectCommentNodes(root)
        }

        // Resolve all found comment nodes
        commentNodes.forEach(commentNode => {
          const commentData = commentNode.getCommentData()
          if (!commentData.resolved) {
            commentNode.setCommentData({
              ...commentData,
              resolved: true,
            })
          }
        })
      },
      { onUpdate: onSaveFile, discrete: true }
    )
  }

  const deleteAllComments = () => {
    editor.update(
      () => {
        // Find all comment nodes in the editor using node traversal
        const root = editor.getEditorState()._nodeMap.get('root')
        const commentNodes: CommentNode[] = []

        const collectCommentNodes = (node: any) => {
          if (node instanceof CommentNode) {
            commentNodes.push(node)
          }
          const children = node.getChildren?.()
          if (children) {
            for (const child of children) {
              collectCommentNodes(child)
            }
          }
        }

        if (root) {
          collectCommentNodes(root)
        }

        // Unwrap all found comment nodes (preserve the text content)
        commentNodes.forEach(commentNode => {
          // Get all child nodes (the text content)
          const children = commentNode.getChildren()

          // Insert children before the comment node
          for (const child of children) {
            commentNode.insertBefore(child)
          }

          // Now remove the empty comment node
          commentNode.remove()
        })
      },
      { onUpdate: onSaveFile, discrete: true }
    )
  }

  const humanReadableBlockType = (blockType: string) => {
    switch (blockType.toUpperCase()) {
      case 'H1':
        return 'Header 1'
      case 'H2':
        return 'Header 2'
      case 'H3':
        return 'Header 3'
      case 'H4':
        return 'Header 4'
      case 'H5':
        return 'Header 5'
      case 'H6':
        return 'Header 6'

      default:
        return blockType
    }
  }

  const applyAllSuggestions = () => {
    editor.dispatchCommand(APPLY_ALL_SUGGESTIONS_COMMAND, undefined)
  }

  const rejectAllSuggestions = () => {
    editor.dispatchCommand(REJECT_ALL_SUGGESTIONS_COMMAND, undefined)
  }

  // Text alignment functions
  const formatAlignment = (alignment: ElementFormatType) => {
    editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, alignment)
    // Update state immediately to keep UI in sync
    setElementFormat(alignment)
  }

  // Callback to update metadata after successful export
  const handleExportSuccess = () => {
    const updateObject = {
      ...currentDocumentMetadata,
      exportCount: (currentDocumentMetadata?.exportCount || 0) + 1,
    }

    setCurrentDocumentMetadata(updateObject)
  }

  return (
    <div className="flex justify-center">
      <button
        onClick={undo}
        disabled={!canUndo}
        className="rounded enabled:hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-none"
      >
        <Undo className="m-2 h-4 w-4 stroke-[2.5]" />
      </button>
      <button
        onClick={redo}
        disabled={!canRedo}
        className="rounded enabled:hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <Redo className="m-2 h-4 w-4 stroke-[2.5]" />
      </button>

      <div className="mx-2 h-6 w-px self-center bg-gray-300" />

      <DropdownMenu>
        <DropdownMenuTrigger className="flex items-center gap-1 rounded px-3 py-2 text-sm hover:bg-gray-200">
          {blockType === 'paragraph' ? 'Normal' : humanReadableBlockType(blockType)}
          <ChevronDown className="h-3 w-3" />
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem onClick={formatParagraph}>Normal</DropdownMenuItem>
          <DropdownMenuItem onClick={() => formatHeading('h1')}>Header 1</DropdownMenuItem>
          <DropdownMenuItem onClick={() => formatHeading('h2')}>Header 2</DropdownMenuItem>
          <DropdownMenuItem onClick={() => formatHeading('h3')}>Header 3</DropdownMenuItem>
          <DropdownMenuItem onClick={() => formatHeading('h4')}>Header 4</DropdownMenuItem>
          <DropdownMenuItem onClick={() => formatHeading('h5')}>Header 5</DropdownMenuItem>
          <DropdownMenuItem onClick={() => formatHeading('h6')}>Header 6</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <div className="mx-2 h-6 w-px self-center bg-gray-300" />

      <button onClick={() => formatText('bold')} className={`rounded hover:bg-gray-200 ${isBold ? 'bg-gray-300' : ''}`}>
        <Bold className="m-2 h-4 w-4 stroke-[2.5]" />
      </button>
      <button
        onClick={() => formatText('italic')}
        className={`rounded hover:bg-gray-200 ${isItalic ? 'bg-gray-300' : ''}`}
      >
        <Italic className="m-2 h-4 w-4 stroke-[2.5]" />
      </button>
      <button
        onClick={() => formatText('underline')}
        className={`rounded hover:bg-gray-200 ${isUnderline ? 'bg-gray-300' : ''}`}
      >
        <Underline className="m-2 h-4 w-4 stroke-[2.5]" />
      </button>
      <button
        onClick={() => formatText('strikethrough')}
        className={`rounded hover:bg-gray-200 ${isStrikethrough ? 'bg-gray-300' : ''}`}
      >
        <Strikethrough className="m-2 h-4 w-4 stroke-[2.5]" />
      </button>
      <button
        onClick={() => formatText('highlight')}
        className={`rounded hover:bg-gray-200 ${isHighlighted ? 'bg-gray-300' : ''}`}
      >
        <Highlighter className="m-2 h-4 w-4 stroke-[2.5]" />
      </button>

      <div className="mx-2 h-6 w-px self-center bg-gray-300" />

      <button
        onClick={() => formatAlignment('left')}
        className={`rounded hover:bg-gray-200 ${elementFormat === 'left' ? 'bg-gray-300' : ''}`}
      >
        <AlignLeft className="m-2 h-4 w-4 stroke-[2.5]" />
      </button>
      <button
        onClick={() => formatAlignment('center')}
        className={`rounded hover:bg-gray-200 ${elementFormat === 'center' ? 'bg-gray-300' : ''}`}
      >
        <AlignCenter className="m-2 h-4 w-4 stroke-[2.5]" />
      </button>
      <button
        onClick={() => formatAlignment('right')}
        className={`rounded hover:bg-gray-200 ${elementFormat === 'right' ? 'bg-gray-300' : ''}`}
      >
        <AlignRight className="m-2 h-4 w-4 stroke-[2.5]" />
      </button>

      <div className="mx-2 h-6 w-px self-center bg-gray-300" />

      <button onClick={() => formatList('ul')} className="rounded hover:bg-gray-200">
        <List className="m-2 h-4 w-4 stroke-[2.5]" />
      </button>
      <button onClick={() => formatList('ol')} className="rounded hover:bg-gray-200">
        <ListOrdered className="m-2 h-4 w-4 stroke-[2.5]" />
      </button>
      <button onClick={outdentContent} className="rounded hover:bg-gray-200">
        <IndentDecrease className="m-2 h-4 w-4 stroke-[2.5]" />
      </button>
      <button onClick={indentContent} className="rounded hover:bg-gray-200">
        <IndentIncrease className="m-2 h-4 w-4 stroke-[2.5]" />
      </button>

      <div className="mx-2 h-6 w-px self-center bg-gray-300" />

      {!hideTrackChanges && (
        <>
          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center gap-1 rounded px-3 py-2 text-sm hover:bg-gray-200">
              <Switch
                className="mr-2"
                disabled
                size="sm"
                isSelected={isTrackChangesEnabled}
                onValueChange={setIsTrackChangesEnabled}
                aria-label="Track Changes"
              />
              Track Changes ({suggestionCount})
              <ChevronDown className="h-3 w-3" />
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={applyAllSuggestions}>Accept all changes</DropdownMenuItem>
              <DropdownMenuItem onClick={rejectAllSuggestions}>Reject all changes</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="mx-2 h-6 w-px self-center bg-gray-300" />
        </>
      )}

      <DropdownMenu>
        <DropdownMenuTrigger className="flex items-center gap-1 rounded px-3 py-2 text-sm hover:bg-gray-200">
          Comments ({commentCount})
          <ChevronDown className="h-3 w-3" />
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem onClick={deleteAllComments}>Remove all comments</DropdownMenuItem>
          <DropdownMenuItem onClick={resolveAllComments}>Accept all comments</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <div className="mx-2 h-6 w-px self-center bg-gray-300" />

      <DropdownMenu>
        <DropdownMenuTrigger className="flex items-center gap-1 rounded px-3 py-2 text-sm hover:bg-gray-200">
          <Download className="m-2 h-4 w-4 stroke-[2.5]" />
          <ChevronDown className="h-3 w-3" />
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem
            onClick={handleExportWord(editor, documentPublicId, getCleanFilename(), onSubscribe, handleExportSuccess)}
          >
            Download as Word
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={handleExportPDF(editor, documentPublicId, getCleanFilename(), onSubscribe, handleExportSuccess)}
          >
            Download as PDF
          </DropdownMenuItem>

          {subscription?.status === 'trialing' && (
            <p className="mt-1 border-t border-gray-200 p-1 text-center text-xs text-gray-500">
              Exports used: {currentDocumentMetadata?.exportCount || 0} of {productMetadata?.trialExportCap ?? 2}
            </p>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* <div className="w-px bg-gray-300 mx-2 h-6 self-center" /> */}

      {/* <button type="button" onClick={() => window.location.href = '/version-comparison'} className="flex items-center hover:bg-gray-200 rounded px-2 py-2 text-sm">
      <FilePlus className="w-4 h-4 stroke-[2.5]" />
      <ArrowRightLeft className="w-4 h-4 stroke-[2.5]" />
      <FileMinus className="w-4 h-4 stroke-[2.5]" />
      <span className="ml-1">Compare</span>
    </button > */}
    </div>
  )
}
